/**
 * Application Capabilities Manifest
 * Describes all capabilities of the app that the AI agent can interact with.
 * This is used to generate the system prompt.
 */

export interface AppCapability {
  name: string;
  description: string;
  category: 'read' | 'write' | 'navigate' | 'knowledge';
}

export const appCapabilities: AppCapability[] = [
  // Read/Query capabilities
  {
    name: 'read_dashboard_kpis',
    description: 'View dashboard KPIs and financial metrics',
    category: 'read',
  },
  {
    name: 'read_records',
    description: 'List all accounting records',
    category: 'read',
  },
  {
    name: 'read_transactions',
    description: 'View transactions with filters (by date, entity, amount, etc.)',
    category: 'read',
  },
  {
    name: 'read_transaction_entities',
    description: 'View transaction entities (actions, objects, media)',
    category: 'read',
  },
  {
    name: 'read_ledgers',
    description: 'View ledger account summaries',
    category: 'read',
  },
  {
    name: 'read_ledger_detail',
    description: 'View detailed ledger for a specific entity',
    category: 'read',
  },
  {
    name: 'read_balance_sheet',
    description: 'View balance sheet (assets, liabilities, capital)',
    category: 'read',
  },
  {
    name: 'read_profit_loss',
    description: 'View profit and loss statement',
    category: 'read',
  },
  {
    name: 'read_stock_valuation',
    description: 'View stock valuation accounts',
    category: 'read',
  },
  {
    name: 'read_trading_accounts',
    description: 'View trading accounts',
    category: 'read',
  },
  {
    name: 'read_credit_balance',
    description: 'View credit points balance',
    category: 'read',
  },
  {
    name: 'read_credit_transactions',
    description: 'View credit transaction history',
    category: 'read',
  },
  {
    name: 'read_subscription',
    description: 'View current subscription details',
    category: 'read',
  },
  {
    name: 'read_plans',
    description: 'View available subscription plans',
    category: 'read',
  },
  {
    name: 'read_notifications',
    description: 'View notifications',
    category: 'read',
  },
  {
    name: 'read_record_users',
    description: 'View users who have access to a record',
    category: 'read',
  },
  {
    name: 'search_entities_by_similarity',
    description: 'Search transaction entities using semantic/cosine similarity matching',
    category: 'read',
  },

  // Write/Action capabilities
  {
    name: 'create_transaction',
    description: 'Create a new transaction in a record',
    category: 'write',
  },
  {
    name: 'update_transaction',
    description: 'Update an existing transaction',
    category: 'write',
  },
  {
    name: 'delete_transaction',
    description: 'Delete a transaction',
    category: 'write',
  },
  {
    name: 'create_record',
    description: 'Create a new accounting record',
    category: 'write',
  },
  {
    name: 'update_record',
    description: 'Update an existing record',
    category: 'write',
  },
  {
    name: 'delete_record',
    description: 'Delete a record (requires verification code)',
    category: 'write',
  },
  {
    name: 'create_entity',
    description: 'Create a new transaction entity (action, object, media)',
    category: 'write',
  },
  {
    name: 'update_entity',
    description: 'Update an existing transaction entity (requires record_id from selected books)',
    category: 'write',
  },
  {
    name: 'delete_entity',
    description: 'Delete a transaction entity',
    category: 'write',
  },
  {
    name: 'join_record',
    description: 'Join a record using an invite code',
    category: 'write',
  },
  {
    name: 'grant_permission',
    description: 'Grant or update user permissions on a record',
    category: 'write',
  },

  // Navigation capabilities
  {
    name: 'navigate_to_page',
    description: 'Navigate the user to any page within the application',
    category: 'navigate',
  },

  // Knowledge capabilities
  {
    name: 'answer_from_knowledge_base',
    description: 'Answer questions using the built-in application knowledge base',
    category: 'knowledge',
  },
  {
    name: 'store_lesson',
    description: 'Save a lesson or tip from the user for future reference',
    category: 'knowledge',
  },
  {
    name: 'retrieve_lessons',
    description: 'Retrieve stored lessons relevant to the current context',
    category: 'knowledge',
  },
];

export function getCapabilitiesByCategory(category: AppCapability['category']): AppCapability[] {
  return appCapabilities.filter(c => c.category === category);
}

export function formatCapabilitiesForPrompt(): string {
  const categories = ['read', 'write', 'knowledge'] as const;

  return categories.map(cat => {
    const items = getCapabilitiesByCategory(cat);
    const header = cat === 'read' ? 'READ TOOLS' :
      cat === 'write' ? 'WRITE TOOLS (confirm card first)' :
        'KNOWLEDGE TOOLS';

    return `${header}:\n${items.map(c => `  - ${c.name}: ${c.description}`).join('\n')}`;
  }).join('\n\n');
}
